import { IDKeyTypes } from 'app/model/id-manager';
import { RoutesService } from './../../model/services/routes.service';
import { DataManagerService, FeatureSelection } from './../../model/services/data-manager.service';
import { Criterion } from './../../model/comparisons';
import { ObjID } from './../../model/id-manager';
import { AttributeFeatureType, ConfigAttribute, CheckboxValue, InputValue, AttributeOptionId, AttributeConfigId } from './../../types/project-config';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, Input, EventEmitter, Output, OnDestroy } from '@angular/core';
import { FeatureData, Project } from 'app/model/project';
import { ComparisonKeys, Comparisons } from 'app/model/comparisons';
import { SubscriptionMgr } from 'app/model/SubscriptionMgr';

@Component({
  selector: 'app-data-entry',
  templateUrl: './data-entry.component.html',
  styleUrls: ['./data-entry.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataEntryComponent implements OnInit, OnDestroy {

  public panelOpen:boolean = true;
  private _ready = false;

  constructor(private cdr: ChangeDetectorRef, private dataMgr: DataManagerService) { }

  private _project?: Project;
  private subscriptions: SubscriptionMgr = new SubscriptionMgr();
  private selection?: FeatureSelection;

  @Input()
  set project(prj: Project | undefined) {
    this._project = prj;
    this.cdr.markForCheck();
    this.Refresh();
  }
  get project(): Project | undefined {
    return this._project;
  }
  private _data: FeatureData = {};
  @Input()
  set data(d: FeatureData) {
    this._data = JSON.parse(JSON.stringify(d));
    this.cdr.markForCheck();
    this.Refresh();
  }
  get data(): FeatureData {
    return this._data;
  }
  private _featureType?: AttributeFeatureType;
  @Input()
  public set featureType(type: AttributeFeatureType | undefined) {
    this._featureType = type;
    this.Refresh();
  }
  public get featureType(): AttributeFeatureType | undefined {
    return this._featureType;
  }

  get attributes(): Array<ConfigAttribute> {
    if (!(this.project && this._featureType)) {
      return [];
    }
    return this.project.GetConfigAttributes(this._featureType);
  }
  attributesChange = new EventEmitter<Array<ConfigAttribute>>();
  @Output()
  public changes = new EventEmitter<FeatureData>();
  ngOnInit(): void {

    this.subscriptions.Subscribe(this.dataMgr.onSelect, (selection: FeatureSelection) => {
      if (!selection?.routePart) {
        return;
      }
      this.selection = selection;
      this._project = this.dataMgr.GetProject();
      this._featureType = this.GetFeatureType(selection.routePart.partId);
      this.data = this.selection.routePart.data as FeatureData;
      this.selection.dataOld = this.CopyData(this.selection.routePart.data);
      this.attributesChange.emit(this.attributes);
      this.cdr.markForCheck();
    });
    this._ready = true;
    this.Refresh();
  }
  GetFeatureType(id: ObjID): AttributeFeatureType | undefined {
    const featureTypeID = this._project?.idManager.IDType(id);
    let type = IDKeyTypes[featureTypeID as number] as string;
    type = type.replace('Feature', '').toLowerCase();
    if (['segment', 'point', 'suppl'].indexOf(type) < 0) return undefined;
    return type as AttributeFeatureType;
  }
  Revert() {
    if (!this.selection?.dataOld) return;
    this.data = this.selection.dataOld;
    this.Refresh();
  }
  CopyData(data?: FeatureData) {
    if (!data) data = this._data;
    return JSON.parse(JSON.stringify(data));
  }
  IsChanged(attributeId: ObjID): boolean {
    if (!this.selection?.changedFields) return false;
    return !!this.selection.changedFields[attributeId];
  }
  HasChanges(): boolean {
    return this.dataMgr.HasChanges();
  }
  Refresh() {
    if (this._ready === true) {
      this.cdr.detectChanges();
    }
  }
  IsSelector(attribute: ConfigAttribute): boolean {
    if (!attribute) return false;
    return (attribute.input === 'selector')
  }
  IsCheckbox(attribute: ConfigAttribute): boolean {
    if (!attribute) return false;
    return (attribute.input === 'checkbox');
  }
  IsInput(attribute: ConfigAttribute): boolean {
    if (!attribute) return false;
    return (attribute.input === 'input')
  }
  IsQty(attribute: ConfigAttribute): boolean {
    if (!attribute) return false;
    return (attribute.input === 'qty')
  }

  IsAvailable(attribute: ConfigAttribute): boolean {
    if (attribute.displayAlways === true) return true;
    const availability = attribute.availability;
    if (!availability) return true;
    const atts = this.attributes;
    let hasMatch = false;
    for (const avail of availability) {
      switch (attribute.input) {
        case 'checkbox':
          hasMatch = hasMatch || (this.data[attribute.id] !== avail.value);
          if (hasMatch) return true;
          break;
        case 'selector':
          if (avail.option_id) {
            hasMatch = hasMatch || ('' + this.data[avail.attribute_id] === '' + avail.option_id);
            if (hasMatch) return true;
          }
          break;
        case 'input':
          const criteria: Criterion = {
            source: this.data[attribute.id] as string,
            comparison: avail.comparison,
            value: avail.value as string
          }
          const comparison = Comparisons[avail.comparison];
          const matched = comparison.Match(criteria);
          if (matched === true) {
            hasMatch = true;

          }
      }

    }
    if (!hasMatch) {
      this._data[attribute.id] = undefined;
    }
    return hasMatch
    // TODO: add availability check
  }
  GetValue(attribute: ConfigAttribute): AttributeOptionId | InputValue | CheckboxValue | undefined {
    if (!(this._data && attribute.id)) return undefined;
    const record = this._data[attribute.id];
    if (!record) {
      if (attribute.default !== undefined) return attribute.default as AttributeOptionId | InputValue | CheckboxValue;
    }
    return record;
  }
  SetValue(attribute: ConfigAttribute, value: AttributeOptionId | InputValue | CheckboxValue | undefined, userChanged = false) {
    if (!this._data && this._featureType) return;
    this._data[attribute.id] = value;
    this.cdr.detectChanges();
    this.Refresh();
    const attributeID: ObjID = attribute.id;
    if (userChanged) {
      if (!!this.selection) {

        if (!this.selection.changedFields) {
          const changes: any = {};
          changes[attributeID] = true;
          this.selection.changedFields = changes;
        } else {
          this.selection.changedFields[attribute.id] = true;
        }
      }
      this.Refresh();
    }
    this.NotifyListeners();
  }
  UpdateFeature() {
    if(!this.selection) return;
    if (!this._data) return;
    const data: any = {};
    for (const att of this.attributes) {
      if (!this.dataMgr.IsAvailable(this.selection.routePart, att, this._data)) {
        continue;
      }
      data[att.id] = this.GetValue(att);
    }
    this.selection.routePart.data = data as FeatureData;

    this.dataMgr.FeatureUpdated('data-entry');

    window.requestAnimationFrame(()=>{
      if(!this.selection) return;
      const routePart = this.dataMgr.GetProject()?.idRegistry.Get(this.selection.routePart.partId as ObjID)
      routePart.style = this.selection.routePart.style;
      routePart.updateUI(true);
      this.dataMgr.SelectFeature(this.selection.routePart.partId, this.selection.route);
    });

  }
  TogglePanel(isOpen?:boolean) {
    this.panelOpen = (isOpen === undefined) ? !this.panelOpen : isOpen;
    this.Refresh();
  }
  NotifyListeners() {
    this.changes.emit(this._data)
  }
  ngOnDestroy(): void {
    this.subscriptions.CancelSubscriptions();
  }

}
